import { useEffect, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { Card, Empty, StatusBadge } from "../panel/PanelShell";
import { Field, SelectField, inputCls } from "../forms/Fields";
import { Textarea } from "../ui/textarea";
import { api, formatApiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";

export function NotificationsTab({ onChange }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/portal/notifications").then((r) => { setRows(r.data); onChange?.(); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const read = async (id) => { await api.post(`/portal/notifications/${id}/read`); load(); };
  const readAll = async () => { await api.post("/portal/notifications/read-all"); load(); };
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><button onClick={readAll} data-testid="notifications-read-all-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-navy"><CheckCheck size={16} /> Mark all read</button></div>
      {rows && !rows.length && <Empty text="No notifications." testid="notifications-empty" />}
      {(rows || []).map((n) => (
        <Card key={n.id} className={`flex items-start gap-4 ${n.read ? "opacity-70" : "border-mint/40"}`} data-testid={`notification-${n.id}`}>
          <Bell size={18} className={n.read ? "text-slate-400" : "text-mint"} />
          <div className="flex-1"><p className="font-semibold text-navy">{n.title}</p><p className="text-sm text-slate-600">{n.message}</p><p className="mt-1 text-xs text-slate-400">{fmtDateTime(n.created_at)}</p></div>
          {!n.read && <button onClick={() => read(n.id)} data-testid={`notification-read-${n.id}`} className="text-xs font-semibold text-mint-600">Mark read</button>}
        </Card>
      ))}
    </div>
  );
}

const CATS = ["General", "Consultation", "Reports", "Account", "Other"];

export function SupportTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ subject: "", message: "", category: "General" });
  const load = () => api.get("/portal/support").then((r) => { setRows(r.data); onChange?.(); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/portal/support", form);
      setForm({ subject: "", message: "", category: "General" });
      toast.success("Support request submitted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_1.3fr]">
      <Card>
        <h3 className="mb-5 font-bold text-navy">New support request</h3>
        <form onSubmit={submit} className="space-y-4" data-testid="support-form">
          <SelectField label="Category" id="s-cat" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATS} testid="support-category-select" />
          <Field label="Subject" id="s-sub"><input id="s-sub" required minLength={3} className={inputCls} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="support-subject-input" /></Field>
          <Field label="Message" id="s-msg"><Textarea id="s-msg" required minLength={5} rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="support-message-input" /></Field>
          <button data-testid="support-submit-btn" className="rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-700">Submit request</button>
        </form>
      </Card>
      <div className="space-y-3">
        {!rows.length && <Empty text="No support requests yet." testid="support-empty" />}
        {rows.map((t) => (
          <Card key={t.id} data-testid={`ticket-${t.id}`}>
            <div className="flex items-start justify-between gap-3"><div><p className="font-semibold text-navy">{t.subject}</p><p className="text-xs text-slate-400">{t.category} · {fmtDateTime(t.created_at)}</p></div><StatusBadge status={t.status} /></div>
            <p className="mt-3 text-sm text-slate-600">{t.message}</p>
            {t.reply && <div className="mt-4 rounded-xl bg-mint/10 p-4 text-sm text-navy"><p className="mb-1 text-xs font-semibold uppercase tracking-wider text-mint-600">Quantora reply</p>{t.reply}</div>}
          </Card>
        ))}
      </div>
    </div>
  );
}
